/**
 * TypeScript interfaces for the Shot Detail Modal feature.
 * These types define the data structures for:
 * - API request/response shapes
 * - Shot and hexbin data structures
 * - Component props
 * - Filter state management
 */
import type {
  ExplorerScope,
  ExplorerSource,
  QueryResolution,
} from "../../models/Explorer";

// ============================================================================
// API Types (matching backend spec)
// ============================================================================

/**
 * Query context passed to shot_detail API endpoint.
 * Contains the minimal filter information to reconstruct a query for specific row data.
 */
export interface ShotQueryContext {
  /** Source table identifier (e.g., "HC_OFFENSE", "HC_DEFENSE") */
  table: string;
  /** Report section name (e.g., "Overall", "By Action") */
  section: string;
  /** Offensive or defensive perspective */
  perspective: "offense" | "defense";
  /** Array of filter tuples: [filterName, operator, [values]] */
  filters: [string, string, string[]][];
  /** The report's scope and source (stamped on every report blob since R1).
   *  Drill-downs forward them so a chart reads the provider the report did. */
  scope?: ExplorerScope | null;
  source?: ExplorerSource | null;
}

/**
 * Request body for POST /api/team_report/shot_detail
 */
export interface ShotDetailRequest {
  /** NBA team ID (e.g., 1610612757 for Trail Blazers) */
  teamNbaId: string;
  /** Season year (e.g., "2024") */
  season: string;
  /** Query context from the report row */
  queryContext: ShotQueryContext;
}

/**
 * Individual shot data from the API response.
 */
export interface Shot {
  /** Court x-coordinate (0-50, where 25 is center court) */
  x: number;
  /** Court y-coordinate (0-47, where 0 is baseline) */
  y: number;
  /** True if shot was made */
  made: boolean;
  /** True if shot was assisted */
  assisted: boolean;
  /** True if shooter was fouled on the attempt */
  fouled: boolean;
  /** True if shot was a three-pointer */
  three: boolean;
  /** Total points scored from this shot attempt (includes FTs from shooting fouls) */
  ptsScored: number;
  /** Shot type classification (null if data source doesn't provide it) */
  shotType: ShotType | null;
  /** Detailed shot type (e.g., "tip", "lob", "pull_up") — null if not provided */
  complexShotType: string | null;
  /** Level of defensive contest (null if data source doesn't provide it) */
  contestLevel: ContestLevel | null;
  /** True if catch-and-shoot attempt (null if data source doesn't provide it) */
  catchAndShoot: boolean | null;
  /** Court region where shot was taken (null if data source doesn't provide it) */
  region: ShotRegion | null;
  /** Shot quality — expected eFG for the shot, 0-100 (SecondSpectrum only) */
  qsq?: number | null;
  /** Player name of the shooter */
  shooter: string;
  /** Player name of the passer (null for unassisted shots) */
  passer: string | null;
  /** Player name of the closest contester (null when uncontested or not provided) */
  contester?: string | null;
  /** Chance/possession ID linking this shot to actions (null if data source doesn't provide it) */
  chanceId: string | null;
}

// Shot metadata types — string rather than narrow unions to support multiple
// data providers (SecondSpectrum/NBA, SkillCorner/G-League) with different values.
export type ShotType = string;
export type ContestLevel = string;
export type ShotRegion = string;

/**
 * Normalize a shot from the API response.
 * Passes through any string values for shotType, contestLevel, etc.
 * Each data provider (SecondSpectrum, SkillCorner) may use different values.
 */
export function normalizeShot(raw: any): Shot {
  return {
    x: raw.x,
    y: raw.y,
    made: raw.made,
    assisted: raw.assisted,
    fouled: raw.fouled,
    three: raw.three,
    ptsScored: raw.ptsScored ?? 0,
    shotType: typeof raw.shotType === "string" ? raw.shotType : null,
    complexShotType:
      typeof raw.complexShotType === "string" ? raw.complexShotType : null,
    contestLevel:
      typeof raw.contestLevel === "string" ? raw.contestLevel : null,
    catchAndShoot:
      typeof raw.catchAndShoot === "boolean" ? raw.catchAndShoot : null,
    region: typeof raw.region === "string" ? raw.region : null,
    shooter: raw.shooter ?? null,
    passer: raw.passer ?? null,
    contester: raw.contester ?? null,
    chanceId: raw.chanceId ?? null,
  };
}

/**
 * Pre-computed aggregations from the API response.
 * Used for displaying filter option counts and summary statistics.
 */
export interface ShotAggregations {
  total: number;
  byOutcome: {
    made: number;
    missed: number;
  };
  byAssisted: {
    assisted: number;
    unassisted: number;
  };
  byShotType: Record<ShotType, number>;
  byContestLevel: Record<ContestLevel, number>;
  byCatchAndShoot: {
    yes: number;
    no: number;
  };
  byRegion: Record<string, number>;
}

/**
 * Full API response from POST /api/team_report/shot_detail
 */
export interface ShotDetailResponse {
  shots: Shot[];
  aggregations: ShotAggregations;
  queryTimeSeconds: number;
  /** The sources the chart read (R3); dev-only label, never product UI. */
  resolution?: QueryResolution;
}

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Shot region categories for filtering.
 * Maps from simple_shot_region values returned by the backend.
 */
export type ShotRegionCategory =
  | "rim"
  | "atb"
  | "corner3"
  | "shortMid"
  | "long2";

/**
 * Map a shot's simple_shot_region to a region category for filtering.
 */
export function getShotRegionCategory(
  region: ShotRegion | null,
): ShotRegionCategory | null {
  if (region === null) return null;
  switch (region) {
    case "rim":
      return "rim";
    case "above the break three":
      return "atb";
    case "corner three":
      return "corner3";
    case "short mid":
      return "shortMid";
    case "long 2":
      return "long2";
    default:
      return null; // far, etc.
  }
}

/**
 * Filter state for the shot detail modal.
 * Each category uses a Set to allow multiple selections (OR logic within category).
 * Selections across categories use AND logic.
 */
export interface ShotFilters {
  /** Filter by assist status */
  assisted: Set<"assisted" | "unassisted">;
  /** Filter by foul status */
  fouled: Set<"fouled" | "not_fouled">;
  /** Filter by shot type (empty = all, data-driven) */
  shotType: Set<string>;
  /** Filter by complex/detailed shot type (empty = all, data-driven) */
  complexShotType: Set<string>;
  /** Filter by contest level (empty = all, data-driven) */
  contestLevel: Set<string>;
  /** Filter by catch-and-shoot */
  catchAndShoot: Set<"yes" | "no">;
  /** Filter by shot region category (empty = all, data-driven) */
  regionCategory: Set<string>;
  /** Filter by shooter name (empty = all shooters) */
  shooter: Set<string>;
  /** Filter by passer name (empty = all passers) */
  passer: Set<string>;
  /** Filter by contester name (empty = all shots, contested or not) */
  contester: Set<string>;
}

/**
 * Default filter state — show all shots.
 * Data-driven categories (shotType, contestLevel, regionCategory) use empty sets
 * meaning "all values pass". This avoids hardcoding provider-specific values.
 */
export const DEFAULT_FILTERS: ShotFilters = {
  assisted: new Set<"assisted" | "unassisted">(["assisted", "unassisted"]),
  fouled: new Set<"fouled" | "not_fouled">(["fouled", "not_fouled"]),
  shotType: new Set<string>(),
  complexShotType: new Set<string>(),
  contestLevel: new Set<string>(),
  catchAndShoot: new Set<"yes" | "no">(["yes", "no"]),
  regionCategory: new Set<string>(),
  shooter: new Set<string>(),
  passer: new Set<string>(),
  contester: new Set<string>(),
};

// ============================================================================
// Hexbin Types
// ============================================================================

/**
 * Data structure for a single hexagonal bin containing aggregated shot data.
 */
export interface HexbinData {
  /** Unique position key for this hexagon (e.g., "123-456") */
  key: string;
  /** Hexagon center x-coordinate (SVG units) */
  x: number;
  /** Hexagon center y-coordinate (SVG units) */
  y: number;
  /** Array of shots in this bin (original, not merged) */
  shots: Shot[];
  /** Number of made shots in bin (original) */
  makes: number;
  /** Total shot attempts in bin (original) */
  attempts: number;
  /** Total points scored in bin (original) */
  ptsScored: number;
  /** Field goal percentage - makes / (makes + missed non-fouled) */
  fgPct: number;
  /** True shooting percentage: ptsScored / attempts / 2 */
  tsPct: number;
  /** Merged attempts count (includes adjacent bins) - used for sizing */
  mergedAttempts: number;
  /** Merged makes count (includes adjacent bins) */
  mergedMakes: number;
  /** Merged points scored (includes adjacent bins) */
  mergedPtsScored: number;
  /** Merged TS% - used for coloring */
  mergedTsPct: number;
  /** Position keys of adjacent hexagons (for hover highlighting) */
  neighborKeys: string[];
}

/**
 * Check if a shot is a 3-pointer.
 */
export function isThreePointShot(shot: Shot): boolean {
  return shot.three;
}

// ============================================================================
// Component Props
// ============================================================================

/**
 * Props for the main ShotDetailModal component.
 */
export interface ShotDetailModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Query context for the API request */
  queryContext: ShotQueryContext;
  /** Team UUID for the API request */
  teamId: string;
  /** Season year for the API request */
  season: string;
  /** Season type for the API request (e.g., "regular season", "playoff") */
  seasonType?: string;
  /** Modal title showing the full context path */
  modalTitle: string;
}

/**
 * Props for the ShotDetailChart component.
 */
export interface ShotDetailChartProps {
  /** Array of shots to display */
  shots: Shot[];
  /** Current filter state */
  filters: ShotFilters;
  /** Chart width in pixels */
  width?: number;
  /** Chart height in pixels */
  height?: number;
  /** Callback when hovering over a hexagon */
  onHexagonHover?: (data: HexbinData | null, event: MouseEvent) => void;
  /** Callback when clicking on a hexagon to select/deselect */
  onHexagonSelect?: (data: HexbinData | null) => void;
  /** Currently selected hexagon key (for persistent highlighting) */
  selectedHexKey?: string | null;
  /** Whether to show the college/G-League three-point line */
  showCollegeLine?: boolean;
  /** Callback when hexbin data changes (for SelectedHexbinStats) */
  onHexbinDataChange?: (data: HexbinData[]) => void;
  /** Override the chart background color (default: "#f8f8f8") */
  bgcolor?: string;
}

/**
 * Props for the SelectedHexbinStats component.
 */
export interface SelectedHexbinStatsProps {
  /** The selected hexbin data (includes neighbor keys for cluster) */
  selectedHexbin: HexbinData | null;
  /** All hexbin data (to find neighbors) */
  allHexbins: HexbinData[];
  /** Shots array for calculating stats based on positions */
  shots: Shot[];
  /** Current filter state */
  filters: ShotFilters;
}

/**
 * Props for the ShotDetailFilters component.
 */
export interface ShotDetailFiltersProps {
  /** All shots (unfiltered) for computing dynamic counts */
  shots: Shot[];
  /** Current filter state */
  filters: ShotFilters;
  /** Callback when filters change */
  onChange: (filters: ShotFilters) => void;
  /** Whether filters are disabled (during loading) */
  disabled?: boolean;
}

/**
 * Props for the ShotDetailSummary component.
 */
export interface ShotDetailSummaryProps {
  /** Array of shots (will be filtered based on current filters) */
  shots: Shot[];
  /** Current filter state */
  filters: ShotFilters;
}

// ============================================================================
// Court Constants
// ============================================================================

/**
 * Court dimension constants in feet (matching existing shot chart implementation).
 */
export const COURT_CONSTANTS = {
  /** Full court width in feet */
  COURT_WIDTH: 50,
  /** Full court length in feet */
  COURT_LENGTH: 94,
  /** Basketball hoop diameter in feet */
  BASKET_DIAMETER: 1.5,
  /** Distance from baseline to backboard in feet */
  BASKET_PROTRUSION_LENGTH: 4,
  /** Backboard width in feet */
  BASKET_WIDTH: 6,
  /** Width of the key (paint) in feet */
  KEY_WIDTH: 16,
  /** Distance from baseline to free throw line in feet */
  FREE_THROW_LINE_LENGTH: 19,
  /** Radius of free throw line circle in feet */
  FREE_THROW_CIRCLE_RADIUS: 6,
  /** Radius of restricted area in feet */
  RESTRICTED_CIRCLE_RADIUS: 4,
  /** NBA three-point line radius in feet */
  THREE_POINT_RADIUS: 23.75,
  /** Distance of corner three from basket in feet */
  THREE_POINT_SIDE_RADIUS: 22,
  /** Distance from baseline where 3pt line becomes circular in feet */
  THREE_POINT_CUTOFF_LENGTH: 14,
  /** College/G-League three-point line radius in feet */
  COLLEGE_THREE_POINT_RADIUS: 22.15,
  /** Width of key marks in feet */
  KEY_MARK_WIDTH: 0.5,
  // One hex size for every hexbin, the drill-down's and the report charts' (Trevor 2026-10-27: 1.5 ft, the print
  // charts' size; 1.0 ft hexes were ~5px at the ownership court's scale), so the same shots fall in the same cells
  HEXAGON_RADIUS: 1.5,
} as const;

/**
 * Calculate visible court length (half court with extra space for title/legend).
 */
export function getVisibleCourtLength(): number {
  const halfCourtLength = COURT_CONSTANTS.COURT_LENGTH / 2;
  const threePointLength =
    COURT_CONSTANTS.THREE_POINT_RADIUS +
    COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH;
  return threePointLength + (halfCourtLength - threePointLength) / 2;
}
